import React from "react";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useUpdateEffect } from "react-use";
import { AssignIcon, Avatar, AvatarGroup, IconButton, Select } from "@activecollab/components";
import classNames from "classnames";
import { isEqual } from "lodash";
import PropTypes from "prop-types";
import {
  getActiveUsersMembersForTaskUserSelector,
  getFilteredUsersFromProjectMembersForTaskUserSelector,
} from "~/store/selectors/selector.users";
import { replaceAvatarSize } from "~/utils/utils";

export const MultiAssigneesPicker = ({
  projectId,
  onChange,
  hiddenFromClients,
  fakeAssigneeName,
  assigneeIds = [],
  canEdit,
  limit = 5,
  className,
}) => {
  const [active, setActive] = useState(false);
  const [selected, setSelected] = useState(assigneeIds);

  const usersSelector = useMemo(() => {
    if (projectId) {
      return getFilteredUsersFromProjectMembersForTaskUserSelector;
    }
    return getActiveUsersMembersForTaskUserSelector;
  }, [projectId]);

  const users = useSelector((state) => usersSelector(state, projectId, hiddenFromClients));

  const options = useMemo(() => {
    if (projectId) {
      const result = users.map((user) => ({
        id: user.id,
        name: user.name,
        image: replaceAvatarSize(user.image, 24),
      }));

      if (fakeAssigneeName?.length) {
        result.push({ id: 0, name: fakeAssigneeName });
      }

      return result;
    }
    return users;
  }, [fakeAssigneeName, projectId, users]);

  const selectedUsers = useMemo(() => users.filter((i) => selected.includes(i.id)), [users, selected]);

  const handleChange = useCallback((value) => {
    setSelected(value);
  }, []);

  const handleClose = useCallback(() => {
    setActive(false);
    if (!isEqual([...selected].sort(), [...assigneeIds].sort())) {
      onChange(selected);
    }
  }, [assigneeIds, onChange, selected]);

  const handleOpen = useCallback(() => {
    setActive(true);
  }, []);

  useUpdateEffect(() => {
    if (assigneeIds.length >= 0 && !isEqual([...selected].sort(), [...assigneeIds].sort())) {
      setSelected(assigneeIds);
    }
  }, [assigneeIds]);

  if (!canEdit) {
    return selectedUsers?.length > 0 ? (
      <AvatarGroup
        limit={limit}
        size={24}
        className="tw-cursor-pointer hover:tw-bg-transparent"
        hasTooltip
        style={{ maxWidth: 24 + limit * 16 }}
      >
        {selectedUsers.map((user) => (
          <Avatar
            key={user.id}
            alt={user.name}
            url={replaceAvatarSize(user.image, 24)}
          />
        ))}
      </AvatarGroup>
    ) : (
      <div className="tw-py-[5px]">
        <AssignIcon />
      </div>
    );
  }

  return (
    <Select
      type="multiple"
      selected={selected}
      options={options}
      onChange={handleChange}
      onSelectClose={handleClose}
      onSelectOpen={handleOpen}
      position="bottom-end"
      target={
        selectedUsers?.length > 0 ? (
          <AvatarGroup
            limit={limit}
            size={24}
            className={classNames(`tw-cursor-pointer hover:tw-bg-transparent tw-py-1`, className)}
            hasTooltip
            style={{ maxWidth: 24 + limit * 16 }}
          >
            {selectedUsers.map((user, index) => (
              <Avatar
                key={user.id}
                alt={user.name}
                url={replaceAvatarSize(user.image, 24)}
                style={{ marginLeft: index === 0 ? 0 : -8 }}
              />
            ))}
          </AvatarGroup>
        ) : (
          <IconButton
            variant="text gray"
            type="button"
            active={active}
            className={classNames(`tw-h-8 tw-py-1 tw-w-[24px]`, className, {
              open: active,
              no_assignee_target: assigneeIds.length <= 0,
            })}
          >
            <AssignIcon className="tw-cursor-pointer" />
          </IconButton>
        )
      }
    />
  );
};

MultiAssigneesPicker.propTypes = {
  projectId: PropTypes.number,
  fakeAssigneeName: PropTypes.string,
  hiddenFromClients: PropTypes.bool,
  canEdit: PropTypes.bool,
  onChange: PropTypes.func,
  assigneeIds: PropTypes.array,
  limit: PropTypes.number,
  className: PropTypes.string,
};
